import type {
    TanstackQueryOptions,
    TanstackSuspenseQueryOptions,
} from "@@/shared/hooks/use-tanstack-query";
import {
    useTanstackMultiQuery,
    useTanstackMultiSuspenseQuery,
} from "@@/shared/hooks/use-tanstack-query";
import { useSingleApiResource } from "@@/shared/state/use-api-resource";
import {
    createQueryKey,
    useApiResources,
} from "@@/shared/state/use-api-resources";
import type { UseQueryResult } from "@tanstack/react-query";
import type {
    AccessKeyId,
    ProductId,
    ProviderId,
    ResourceGlobal,
    ResourceGraph,
    ResourceId,
} from "@towni/common";
import { resourcesMainQueryKey } from "../main-query-key";
import { fetchResource } from "./resource.fetcher";
import {
    fetchBookableResourcesForProduct,
    fetchResourcesForProviderRequests,
} from "./resources.fetcher";

const useResourcesForProvider = (
    providerId?: ProviderId,
): readonly [ResourceGlobal[], UseQueryResult<ResourceGlobal[]>] => {
    const id = providerId ?? "";
    return useApiResources<ResourceGlobal>(
        createQueryKey(resourcesMainQueryKey, id),
        fetchResourcesForProviderRequests(providerId),
        {
            enabled: !!providerId,
        },
    );
};

const useResource = (
    resourceId?: ResourceId,
    accessKey?: AccessKeyId,
): readonly [
    ResourceGlobal | undefined,
    UseQueryResult<ResourceGlobal, unknown>,
] => {
    const id = resourceId ?? "";
    return useSingleApiResource<ResourceGlobal>(
        [resourcesMainQueryKey, id],
        fetchResource(resourceId, accessKey),
        {
            enabled: !!resourceId,
        },
    );
};

/**
 * Bookable flattened resources
 * @deprecated  use useProductResources instead, same same but different (and better) */
const useBookableResources = (
    productId?: ProductId,
): readonly [ResourceGraph[], UseQueryResult<ResourceGraph[]>] => {
    return useApiResources<ResourceGraph>(
        createQueryKey(resourcesMainQueryKey, productId ?? ""),
        fetchBookableResourcesForProduct(productId),
        {
            enabled: !!productId,
        },
    );
};

const useProductResources = (
    productId?: ProductId,
    options: TanstackQueryOptions<ResourceGraph[]> = {},
) => {
    return useTanstackMultiQuery<ResourceGraph>(
        `/resources/for-product/${encodeURIComponent(productId || "")}`,
        [resourcesMainQueryKey, productId],
        {
            ...options,
            enabled: !!productId,
        },
    );
};

const useProductResourcesSuspendedQuery = (
    productId: ProductId | undefined,
    options: TanstackSuspenseQueryOptions<ResourceGraph[]> = {},
) => {
    return useTanstackMultiSuspenseQuery<ResourceGraph>(
        `/resources/for-product/${encodeURIComponent(productId || "")}`,
        [resourcesMainQueryKey, productId],
        {
            ...options,
            disabled: !productId,
        },
    );
};

export {
    useBookableResources,
    useProductResources,
    useProductResourcesSuspendedQuery,
    useResource,
    useResourcesForProvider,
};
